import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Search,
  CalendarCheck,
  IdCard,
  KeyRound,
  Route,
  Star,
  Car,
  TrendingUp,
  DollarSign,
  ShieldCheck,
  Zap,
  ArrowRight,
  Clock,
  FileX,
  Users,
} from "lucide-react";
import useSeo from "../hooks/useSeo";

const GUEST_STEPS = [
  {
    n: "01",
    icon: Search,
    title: "Explora y encuentra tu auto ideal",
    desc: "Busca vehículos disponibles en tu zona directamente desde la app o sitio web.",
    bullets: [
      "Filtra por precio, tipo de auto, ubicación y fechas",
      "Desde autos económicos hasta opciones premium",
      "Todo disponible en tiempo real",
    ],
  },
  {
    n: "02",
    icon: CalendarCheck,
    title: "Reserva en segundos",
    desc: "Selecciona el vehículo, elige tus fechas y confirma tu reserva.",
    bullets: ["Sin filas", "Sin papeleo", "100% digital · pago seguro desde la plataforma"],
  },
  {
    n: "03",
    icon: IdCard,
    title: "Verificación rápida y segura",
    desc: "Para garantizar la seguridad de todos los usuarios:",
    bullets: ["Identificación oficial", "Licencia de conducir válida", "Selfie con tu ID · aprobación rápida"],
  },
  {
    n: "04",
    icon: KeyRound,
    title: "Recoge tu auto",
    desc: "Coordina con el anfitrión (dueño del vehículo):",
    bullets: [
      "Entrega en ubicación acordada o punto estratégico",
      "Algunos autos incluyen entrega a domicilio o aeropuerto",
      "Comunicación directa en la app",
    ],
  },
  {
    n: "05",
    icon: Route,
    title: "Disfruta tu viaje",
    desc: "Usa el vehículo durante tu periodo de renta con total libertad.",
    bullets: ["Autos verificados", "Experiencia segura", "Soporte ante cualquier situación"],
  },
  {
    n: "06",
    icon: Star,
    title: "Califica tu experiencia",
    desc: "Al finalizar tu renta:",
    bullets: ["Califica al anfitrión", "Califica el vehículo", "Mantén la calidad y confianza de Rivones"],
  },
];

const HOST_STEPS = [
  { icon: Car, title: "Publica tu vehículo", desc: "Sube fotos, detalles y disponibilidad." },
  { icon: TrendingUp, title: "Recibe solicitudes", desc: "Acepta reservas fácilmente desde la app." },
  { icon: DollarSign, title: "Genera ingresos", desc: "Convierte tu auto en una fuente de dinero." },
];

const DIFF = [
  { icon: Zap, text: "Sin filas" },
  { icon: FileX, text: "Sin papeleo" },
  { icon: Clock, text: "Más rápido que rentadoras tradicionales" },
  { icon: Users, text: "Comunidad confiable" },
];

export default function ComoFuncionaPage() {
  useSeo({
    title: "Cómo funciona Rivones · Renta de autos entre particulares en México",
    description:
      "Aprende cómo rentar un auto en Rivones en 6 pasos: explora, reserva, verifica tu identidad, recoge el auto, disfruta y califica. También descubre cómo ganar dinero publicando tu auto.",
    keywords:
      "cómo funciona renta de autos, cómo rentar auto en México, renta de autos entre particulares, publicar auto, ganar dinero con mi auto, Rivones",
    canonical: "https://rivones.com/como-funciona",
  });

  return (
    <div className="bg-sand min-h-screen" data-testid="como-funciona-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-neutral-50 to-sand border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
          <p className="text-sm font-semibold tracking-[0.2em] text-terracotta uppercase">
            Cómo funciona
          </p>
          <h1
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#111111] tracking-tight mt-3"
            data-testid="como-h1"
          >
            Renta autos entre particulares — rápido, seguro y sin complicaciones
          </h1>
          <p className="text-lg text-neutral-600 mt-5 max-w-2xl mx-auto">
            En Rivones reservas, conduces y ganas dinero con tu auto. Todo desde tu celular.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-7">
            <Link to="/search">
              <Button className="btn-primary" data-testid="como-cta-search">
                Buscar un auto <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/host/listings?new=1">
              <Button variant="outline" className="h-11 rounded-xl border-neutral-300" data-testid="como-cta-host">
                Publicar mi auto
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Guest 6 steps */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Para huéspedes</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#111111] mt-1">
              Reserva tu auto en 6 pasos
            </h2>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {GUEST_STEPS.map(({ n, icon: Icon, title, desc, bullets }) => (
            <article
              key={n}
              data-testid={`como-step-${n}`}
              className="relative bg-white rounded-2xl p-5 sm:p-6 border border-neutral-200 hover:border-neutral-300 hover:shadow-sm transition"
            >
              <div className="flex items-start justify-between mb-4">
                <span className="inline-flex w-12 h-12 rounded-xl bg-neutral-100 items-center justify-center">
                  <Icon className="w-6 h-6 text-[#111111]" strokeWidth={2} />
                </span>
                <span className="font-heading font-bold text-3xl text-neutral-200 leading-none">
                  {n}
                </span>
              </div>
              <h3 className="font-heading font-semibold text-lg leading-snug">{title}</h3>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{desc}</p>
              <ul className="mt-3 space-y-1.5">
                {bullets.map((b) => (
                  <li key={b} className="text-sm text-neutral-700 flex gap-2">
                    <span className="text-terracotta mt-1">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* Hosts block */}
      <section className="bg-[#111111] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-terracotta font-semibold">Para anfitriones</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2 text-white">
              ¿Tienes un auto? Gana dinero con Rivones
            </h2>
            <p className="text-neutral-300 mt-3 max-w-xl mx-auto">
              Publícalo una vez, acepta reservas cuando quieras y genera ingresos en piloto automático.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
            {HOST_STEPS.map(({ icon: Icon, title, desc }, i) => (
              <article
                key={title}
                className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 backdrop-blur-md"
              >
                <span className="inline-flex w-11 h-11 rounded-xl bg-terracotta/15 items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-terracotta" />
                </span>
                <p className="text-xs text-neutral-400 font-semibold">PASO {i + 1}</p>
                <h3 className="font-heading font-semibold text-lg mt-1 text-white">{title}</h3>
                <p className="text-sm text-neutral-300 mt-2 leading-relaxed">{desc}</p>
              </article>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/host/listings?new=1">
              <Button className="btn-primary" data-testid="como-host-cta">
                Publicar mi auto <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="bg-white rounded-3xl border border-neutral-200 p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex w-11 h-11 rounded-xl bg-emerald-100 items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-emerald-600" />
            </span>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl">Seguridad y confianza</h2>
          </div>
          <p className="text-neutral-600 leading-relaxed">
            En Rivones priorizamos tu tranquilidad. Cada renta está respaldada por:
          </p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm text-neutral-700">
            <p className="flex gap-2"><span className="text-emerald-600">✔</span> Verificación de usuarios</p>
            <p className="flex gap-2"><span className="text-emerald-600">✔</span> Sistema de calificaciones</p>
            <p className="flex gap-2"><span className="text-emerald-600">✔</span> Pagos protegidos</p>
            <p className="flex gap-2"><span className="text-emerald-600">✔</span> Soporte al cliente</p>
          </div>
        </div>
      </section>

      {/* Differentiator strip */}
      <section className="bg-neutral-100 border-y border-neutral-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-5">
            Rivones es diferente
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {DIFF.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3 text-sm font-medium text-[#111111]">
                <span className="inline-flex w-9 h-9 rounded-lg bg-white border border-neutral-200 items-center justify-center">
                  <Icon className="w-4 h-4 text-terracotta" />
                </span>
                {text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA footer */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 text-center">
        <h2 className="font-heading font-bold text-3xl sm:text-4xl text-[#111111]">
          Empieza ahora
        </h2>
        <p className="text-neutral-600 mt-3 max-w-xl mx-auto">
          Entra a la plataforma y vive una nueva forma de rentar autos.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
          <Link to="/search">
            <Button className="btn-primary" data-testid="como-footer-search">
              Buscar un auto <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/login?mode=register">
            <Button variant="outline" className="h-11 rounded-xl border-neutral-300" data-testid="como-footer-register">
              Crear cuenta
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
